import { useCallback, useRef, useState } from 'react';

/**
 * A custom hook that tracks element visibility using the IntersectionObserver API.
 * Returns a boolean indicating visibility, along with a ref-callback to bind
 * the target element. Automatically handles subscription, cleanup, and
 * prevents duplicate observer instances.
 *
 * finishWhenVisible, finishWhenHidden - when the Observer changes the visible state, it will be stop watching
 * threshold - how partially the element should be visible.
 * onChange - optional fn that will call with state, instead of local state (isVisible) (for example, it helpfully if you don't want to rerender the component)
 */
const useIsVisible = ({
  finishWhenVisible = false,
  finishWhenHidden = false,
  threshold = 0.75,
  initState = false,

  onChange,
} = {}) => {
  const [isVisible, setIsVisible] = useState(initState);
  // as a regular component ref
  const currentComponentRef = useRef();

  const intersectionObserverRef = useRef(null);

  // unsubscribe IntersectionObserver for preventing memory leak
  const unsubscribe = useCallback(() => {
    if (currentComponentRef.current && intersectionObserverRef.current) {
      intersectionObserverRef.current.unobserve(currentComponentRef.current);
    }
  }, []);

  // subscribe IntersectionObserver
  const subscribe = useCallback(() => {
    if (currentComponentRef.current && intersectionObserverRef.current) {
      intersectionObserverRef.current.observe(currentComponentRef.current);
    }
  }, []);

  const bindingRefCallback = useCallback((node) => {
    // before unmounting refCallback returns null
    if (!node) {
      unsubscribe();
      return;
    }

    // prohibition of double appropriation
    // usually never happens, but just in case
    if (intersectionObserverRef.current) {
      return;
    }

    // set refs
    currentComponentRef.current = node;

    intersectionObserverRef.current = new IntersectionObserver(
      (entries) => {
        // Assuming there's only one element being observed
        const isVisible = entries[0].isIntersecting;
        const changeFn = onChange || setIsVisible;
        changeFn(isVisible);

        if (isVisible && finishWhenVisible) {
          unsubscribe();
        }
        if (!isVisible && finishWhenHidden) {
          unsubscribe();
        }
      },
      { threshold }
    );

    subscribe();
  }, []);

  return {
    // main
    // use as ref for the tracking component
    bindingRefCallback,

    //additional
    isVisible,
    currentComponentRef,
    unsubscribe,
    subscribe,
  };
};

export default useIsVisible;
